import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Home, PhoneCall, Headphones, ClipboardList, Megaphone, Check } from "lucide-react";
import Reveal from "./Reveal";
import ServiceModal from "./ServiceModal";
import { useSite } from "@/context/SiteContext";

const SERVICES = [
  {
    icon: ShoppingCart,
    title: "E-commerce Support",
    img: "https://images.unsplash.com/photo-1762341118954-d0ce391674d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Customer support (email, chat, live chat)", "Order processing and tracking", "Returns, refunds, and issue resolution", "Product listing and inventory updates"],
  },
  {
    icon: Home,
    title: "Real Estate Operations",
    img: "https://images.unsplash.com/photo-1600275669283-4bf2bb8a990c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Listing management and data entry", "Transaction coordination", "Client communication and follow-ups", "Calendar and showing management"],
  },
  {
    icon: PhoneCall,
    title: "Appointment Setting",
    img: "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Outbound lead calling and follow-ups", "Appointment scheduling and confirmations", "Inbound customer support", "CRM updates and call logging"],
  },
  {
    icon: Headphones,
    title: "Cold Calling & Phone Support",
    img: "https://images.unsplash.com/photo-1685760259914-ee8d2c92d2e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Outbound calling and lead generation", "Customer inquiries and support", "Follow-ups and re-engagement", "CRM updates and reporting"],
  },
  {
    icon: ClipboardList,
    title: "Administrative & Executive Assistance",
    img: "https://images.unsplash.com/photo-1758691737605-69a0e78bd193?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Inbox and calendar management", "Data entry and document preparation", "Travel arrangements and research", "General administrative support"],
  },
  {
    icon: Megaphone,
    title: "Social Media Management & Content",
    img: "https://images.unsplash.com/photo-1603714228681-b399854b8f80?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bullets: ["Content scheduling and publishing", "Community engagement and moderation", "Basic graphic creation and video editing", "Performance tracking and reporting"],
  },
];

const dirFor = (i) => (i % 3 === 0 ? "left" : i % 3 === 2 ? "right" : "up");

function CheckBullet({ children }) {
  return (
    <li className="flex items-start gap-3 text-[14px] text-[#C3CFE3] leading-relaxed">
      <span className="w-[19px] h-[19px] rounded-full bg-[#CFE6FF] flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check size={11} className="text-[#1D6FF2]" strokeWidth={3.5} />
      </span>
      <span>{children}</span>
    </li>
  );
}

export default function Services() {
  const { content } = useSite();
  const [selected, setSelected] = useState(null);
  const detailsFor = (title) => (content.service_details?.services || []).find((d) => d.title === title) || {};

  return (
    <section
      id="services"
      className="section-pad relative overflow-hidden text-white"
      style={{ backgroundImage: "url(/services-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <Reveal direction="left">
          <div className="eyebrow text-[#00CFFF] mb-5">What we take off your plate</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[46px] leading-[1.15] tracking-tight max-w-3xl">
            Skilled support for your <span className="text-gradient">everyday operations.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
            Trained remote specialists who handle the work, so you can stay focused on what matters most.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((s, i) => {
            const details = detailsFor(s.title);
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, x: dirFor(i) === "left" ? -48 : dirFor(i) === "right" ? 48 : 0, y: dirFor(i) === "up" ? 40 : 0 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-2xl overflow-hidden border border-[rgba(120,170,255,0.18)] bg-[rgba(13,31,60,0.55)] backdrop-blur-md shadow-[0_18px_50px_rgba(0,0,0,0.35),0_0_30px_rgba(29,111,242,0.12)] transition-all duration-300 hover:border-[rgba(120,170,255,0.42)] hover:shadow-[0_26px_70px_rgba(0,0,0,0.45),0_0_46px_rgba(29,111,242,0.24)] hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelected({ ...s, ...details })}
                data-testid={`service-card-${i}`}
              >
                <div className="relative h-52">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(10,22,40,0) 38%, rgba(10,22,40,0.55) 76%, rgba(10,22,40,0.92) 100%)" }}
                  />
                </div>
                <div className="px-6 pb-7 relative">
                  <div className="flex items-center gap-4 -mt-7 relative z-10">
                    <span className="w-[58px] h-[58px] rounded-full bg-[#E8F2FF] border-[3px] border-[#0D1F3C] shadow-lg flex items-center justify-center flex-shrink-0">
                      <s.icon size={24} className="text-[#1D6FF2]" strokeWidth={2.2} />
                    </span>
                    <h3 className="font-display font-extrabold text-white text-[19px] leading-snug pt-4" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}>{s.title}</h3>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {s.bullets.map((b) => <CheckBullet key={b}>{b}</CheckBullet>)}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <ServiceModal service={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
